import { parseWithZod } from '@conform-to/zod';
import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { data, useFetcher } from 'react-router';
import { useAuthenticityToken } from 'remix-utils/csrf/react';
import { z } from 'zod';

import { getHints } from '~/components/client-hints';
import { StatusButton } from '~/components/ui/status-button';
import { type GymPlan } from '~/domain/plans/handlers/get-plans-for-gym.server';
import {
  createMembershipPayment,
  type CreateMembershipPaymentResponse,
} from '~/domain/plans-and-memberships/handlers/create-membership-payment.server';
import { type UserMembership } from '~/domain/user-memberships/models/user-membership';
import { requireSession } from '~/utils/auth.server';
import { validateCSRF } from '~/utils/csrf.server';
import { fromStripeFormat } from '~/utils/currency';
import { handleDomainError } from '~/utils/errors.server';
import { useFetcherWithPromise } from '~/utils/fetcher-with-promise';
import { getGymIdAndSlugFromParams } from '~/utils/gyms.server';

import {
  DefaultPromoCodeHelperContent,
  type PromoCode,
  PurchaseModal,
} from '../components/purchase-modal';
import { PurchaseSelect } from '../components/purchase-select';
import { PurchaseSummary } from '../components/purchase-summary';

import { type Route } from './+types/purchase-membership';
import { type PromoCodeValidationResponse } from './validate-promo';

const schema = z.object({
  gymPlanId: z.coerce.number(),
  promoCode: z.string().optional(),
});

export async function action({ request, params }: Route.ActionArgs) {
  const session = await requireSession(request);
  const { gymId } = await getGymIdAndSlugFromParams(params);
  const { timeZone } = getHints(request);

  const formData = await request.formData();
  await validateCSRF(formData, request.headers);

  const submission = parseWithZod(formData, { schema });
  if (submission.status !== 'success') {
    return submission.reply();
  }

  const { promoCode, gymPlanId } = submission.value;

  const res = await createMembershipPayment(session, timeZone, gymId, gymPlanId, promoCode);
  if (res.isErr()) {
    return handleDomainError(res);
  }

  return data(res.value);
}

export interface PurchaseMembershipButtonProps {
  gymSlugOrId: string;
  gymPlans: GymPlan[];
  userMembership: UserMembership | null;
}

export function PurchaseMembershipButton({
  gymPlans,
  gymSlugOrId,
  userMembership,
}: PurchaseMembershipButtonProps) {
  const [showingModal, setShowingModal] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<PromoCode | undefined>();
  const [amount, setAmount] = useState<number | undefined>();
  const [amountBelowMinimum, setAmountBelowMinimum] = useState(false);
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const [intentType, setIntentType] = useState<'payment' | 'setup' | undefined>();

  const fetcher = useFetcher<CreateMembershipPaymentResponse>();
  const validatePromoFetcher = useFetcherWithPromise<PromoCodeValidationResponse>();
  const csrf = useAuthenticityToken();
  const [selectedPlan, setSelectedPlan] = useState<GymPlan | null>(null);

  const formattedPurchasedDate = userMembership
    ? format(userMembership.purchasedAt, 'MM/dd/yyyy')
    : '';
  const formattedValidToDate = userMembership
    ? format(userMembership.billingTermEndAt, 'MM/dd/yyyy')
    : '';
  const formattedValidFromDate = userMembership
    ? format(userMembership.billingTermStartAt, 'MM/dd/yyyy')
    : '';

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      setStripeClientSecret(fetcher.data.clientSecret);
      setPromoCode(fetcher.data.promoCode);
      setAmount(fetcher.data.amount);
      setAmountBelowMinimum(fetcher.data.isBelowMinimum);
      setPromoCodeInvalid(fetcher.data.promoCodeInvalid);
      setIntentType(fetcher.data.intentType);
      setShowingModal(true);
    }
  }, [fetcher, setShowingModal, setStripeClientSecret]);

  const handleShowModal = () => {
    const body = new FormData();
    body.append('csrf', csrf);
    body.append('gymPlanId', (selectedPlan?.id ?? 0).toString());

    fetcher.submit(body, {
      method: 'POST',
      action: `/gym/${gymSlugOrId}/resources/purchase-membership`,
    });
  };

  const handleModalClose = () => {
    setShowingModal(false);
    setPromoCode(undefined);
    setAmount(undefined);
    setAmountBelowMinimum(false);
    setPromoCodeInvalid(false);
    setIntentType(undefined);
  };

  const sortedGymPlans = [...gymPlans].sort((a, b) => a.finalPriceCents - b.finalPriceCents);
  const handleSelectChange = (planId: string) => {
    const selectedPass = gymPlans.find((pass) => pass.id.toString() === planId);
    setSelectedPlan(selectedPass || null);
  };

  const handlePromoCodeSubmit = (promoCode: string) => {
    fetcher.submit(
      {
        csrf,
        gymPlanId: (selectedPlan?.id ?? 0).toString(),
        promoCode,
      },
      {
        method: 'POST',
        action: `/gym/${gymSlugOrId}/resources/purchase-membership`,
      },
    );
  };

  const handlePaymentPreCheck = useCallback(async (): Promise<string | null> => {
    if (!promoCode) {
      return null;
    }

    const data = await validatePromoFetcher.submit(
      { csrf, promoCodeName: promoCode.name },
      {
        method: 'POST',
        action: `/gym/${gymSlugOrId}/resources/validate-promo`,
      },
    );

    if (!data?.valid) {
      return 'This promo code is no longer valid.';
    }

    return null;
  }, [csrf, promoCode, validatePromoFetcher, gymSlugOrId]);

  const isSubmitting = fetcher.state !== 'idle';
  const termLabel = selectedPlan
    ? `Term: ${selectedPlan.billingTermMonths} month${selectedPlan.billingTermMonths === 1 ? '' : 's'}`
    : '';

  return (
    <>
      {userMembership === null ? (
        <div className="mt-2 flex flex-col gap-4 text-body2">
          <p>
            Ready to get serious? Memberships provide consistent access to expert instruction,
            allowing you to refine your technique, achieve your goals, and join the community.
          </p>

          <p>
            Memberships automatically renew at the end of each term. Your chosen payment method will
            be charged the current rate. Cancel anytime.
          </p>

          <div className="flex items-center gap-4">
            <PurchaseSelect
              items={sortedGymPlans.map(({ id, name, finalPriceCents }) => ({
                id: id.toString(),
                name,
                priceCents: finalPriceCents,
              }))}
              onChange={handleSelectChange}
              placeholder="Select a membership…"
            />
            <StatusButton
              className="h-[30px] min-w-[100px] whitespace-nowrap"
              onClick={handleShowModal}
              disabled={selectedPlan === null || isSubmitting}
              type="submit"
              color="primary"
              status={isSubmitting ? 'pending' : 'idle'}
            >
              Join Now
            </StatusButton>
          </div>

          {selectedPlan ? (
            <>
              <p>{termLabel}</p>

              {selectedPlan.description ? <p>{selectedPlan.description}</p> : null}
            </>
          ) : null}
        </div>
      ) : (
        <PurchaseSummary
          purchaseName={userMembership.name}
          purchaseItems={[
            { name: 'Purchased', value: formattedPurchasedDate },
            { name: 'Price', value: fromStripeFormat(userMembership.amount) },
            {
              name: 'Valid Through',
              value: (
                <>
                  <time dateTime={userMembership?.billingTermStartAt.toISOString()}>
                    {formattedValidFromDate}
                  </time>{' '}
                  -{' '}
                  <time dateTime={userMembership?.billingTermEndAt.toISOString()}>
                    {formattedValidToDate}
                  </time>
                </>
              ),
            },
            {
              name: 'Term: ',
              value: `${userMembership.billingTermMonths} month${userMembership.billingTermMonths === 1 ? '' : 's'}`,
              className: 'justify-start mt-3 gap-1',
              valueClassName: 'text-left',
            },
            {
              name: '',
              value: userMembership.description,
              className: `justify-start mt-3 ${userMembership.description ? '' : 'hidden'}`,
              valueClassName: 'text-left',
            },
          ]}
        />
      )}

      {showingModal && stripeClientSecret !== null ? (
        <PurchaseModal
          open
          onClose={handleModalClose}
          stripeClientSecret={stripeClientSecret}
          returnUrl={`${window.location.protocol}//${window.location.host}/gym/${gymSlugOrId}/resources/purchase-membership-return`}
          title="Pay & Join"
          stripePaymentButtonLabel={intentType === 'setup' ? 'Redeem at no Cost' : 'Pay & Join'}
          className="pt-0"
          onPromoCodeSubmit={handlePromoCodeSubmit}
          promoCode={promoCode}
          promoCodeInvalid={promoCodeInvalid}
          isLoading={isSubmitting}
          onPrePaymentCheck={handlePaymentPreCheck}
          intentType={intentType}
          helperContent={
            <DefaultPromoCodeHelperContent
              amount={amount}
              amountBelowMinimum={amountBelowMinimum}
              promoOnFirstPaymentOnly={promoCode?.appliesToFirstPaymentOnly}
              recurring
            />
          }
          content={
            <div className="flex flex-col">
              <p className="text-subtitle2 font-light leading-none">{selectedPlan?.name}</p>
              <p className="text-subtitle2 font-light">{termLabel}</p>
              <p className="my-3.5 text-caption italic leading-none">
                Memberships automatically renew at the end of each term. Prices are subject to
                change. Your chosen payment method will be charged the current rate. Cancel anytime.
              </p>
              <p className="font-medium">
                {selectedPlan?.finalPriceCents
                  ? fromStripeFormat(amount ?? selectedPlan.finalPriceCents)
                  : null}
              </p>
              <p className="text-caption font-light">{fetcher.data?.gymName ?? ''}</p>
            </div>
          }
        />
      ) : null}
    </>
  );
}
